import { errorMessage } from "@api/errors";
import type {
  BreederTrialsApplicationNumberingCopyRow as CopyRow,
  NumberingRun,
  ParallelDraft,
  ParallelTrialsIn,
} from "@api/generated/model";
import {
  numberingParallelsPreview,
  useNumberingParallelsRun,
  useNumberingPlots,
  useNumberingTrials,
} from "@api/generated/numbering/numbering";
import { useUserMe } from "@api/generated/user/user";
import { usePreview, useRunResult } from "@app/preview";
import { useInvalidate } from "@app/queryClient";
import { hasRole, useAutosave, useSeasonFromLink, useSettings } from "@app/workspace";
import { SavedQuerySelect, useSavedQuery } from "@features/queries/SavedQuerySelect";
import { plotKey } from "@features/trials/crossing";
import { ParallelTable } from "@features/trials/NumberingParts";
import { nextParallel, type ParallelSpec, parallelsOf } from "@features/trials/numbering";
import { hint } from "@i18n";
import { QueryFeedback } from "@ui/QueryFeedback";
import "@features/trials/numbering.css";
import { ResultBanner } from "@features/system/undo";
import { Button, Checkbox, Input, Loader, SegmentedControl, Select } from "@mantine/core";
import { createFileRoute, Link } from "@tanstack/react-router";
import { formatNumber } from "@ui/format";
import { PageHeader } from "@ui/PageHeader";
import { RunBar } from "@ui/RunBar";
import { type Column, DataTable, useDataTable } from "@ui/table/DataTable";
import { YearToggle } from "@ui/YearToggle";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const PREVIEW_KEY = "/api/v1/numbering/parallels/preview";
const CHANGED = /\/api\/v1\/numbering\/(trials|parallels)/; // what a run changes: the trials' plots, the preview
const SHOWN = 500; // copies listed (the counts are the whole plan's)
type Source = "trial" | "query";
type Draft = { parallels: ParallelSpec[]; main_line: boolean };

export const Route = createFileRoute("/season/parallel-experiments")({
  validateSearch: z.object({ ev: z.number().int().optional(), kis: z.string().optional(), next: z.boolean().optional() }),
  component: ParallelTrialsPage,
});

/** Tenyészidőszak → Párhuzamos kísérletek (the legacy Applications → Parallel Experiments): the plots of a numbered
 *  trial, or a saved query's plots of the year in the query's order (legacy cmdParExpQuerybol), copied into parallel
 *  trials (LR, SR, FUZ …) of the same year — numbered on from a start, the code's last part replaced, a suffix added
 *  or the code kept, some plots left out — with an opening ledger row each; one undoable operation. Plot numbering
 *  makes the parallels of the lines it numbers; this page adds them to plots numbered already. The source and the
 *  parallel trials are kept with the user's settings. */
function ParallelTrialsPage() {
  const { t } = useTranslation();
  const settings = useSettings();
  const canWrite = hasRole(useUserMe().data?.roles, "nemesito");
  if (!canWrite) {
    return (
      <div className="page">
        <PageHeader title={t("parallelTrials.title")} subtitle={t("parallelTrials.subtitle")} season />
        <p className="mu">{t("parallelTrials.noPermission")}</p>
      </div>
    );
  }
  if (!settings) return <Loader size="sm" m="md" />;
  return <ParallelTrials stored={settings.parallel_draft ?? null} />;
}

function ParallelTrials(props: { stored: ParallelDraft | null }) {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const season = useSeasonFromLink(search.ev);
  const next = search.next ?? true; // parallel trials are usually made for the next season, after its numbering
  const ev = season != null ? season + (next ? 1 : 0) : undefined;
  const [init] = useState(() => props.stored);
  const [source, setSource] = useState<Source>(init?.source ?? "trial");
  const [query, setQuery] = useState<number | null>(init?.query ?? null);
  const stored = (init?.spec ?? {}) as Partial<Draft>;
  const [parallels, setParallels] = useState<ParallelSpec[]>(() => stored.parallels ?? [nextParallel([])]);
  const [mainLine, setMainLine] = useState(stored.main_line ?? false);
  const draft = useMemo<ParallelDraft>(
    () => ({ source, query, spec: { parallels, main_line: mainLine } as never }),
    [source, query, parallels, mainLine],
  );
  useAutosave("parallel_draft", draft, init);

  const trials = (useNumberingTrials({ ev: ev ?? 0 }, { query: { enabled: !!ev } }).data ?? []).filter((x) => x.first != null);
  const kis = source === "trial" ? (search.kis ?? null) : null;
  const plots = useNumberingPlots({ ev: ev ?? 0, kis: kis ?? "" }, { query: { enabled: !!ev && !!kis } }).data ?? [];
  const saved = useSavedQuery(query, source === "query");
  const chosenQuery = saved.query;
  const invalidate = useInvalidate(CHANGED);
  // a query's conditions choose its plots: nothing is left out one by one
  const ready = parallelsOf(source === "query" ? parallels.map((p) => ({ ...p, exclude: [] })) : parallels);
  const complete = ready.length > 0 && ready.length === parallels.length;
  const request: ParallelTrialsIn | null =
    ev && complete && (kis || chosenQuery)
      ? { ev, kis, query: chosenQuery ? chosenQuery.id : null, parallels: ready, main_line: mainLine }
      : null;
  const preview = usePreview(`${PREVIEW_KEY}:${saved.stamp}`, request, (body, signal) => numberingParallelsPreview(body, { signal }));
  const result = preview.data;
  const taken = new Set(result?.taken);
  // the last run, shown while the request is the one it ran (any change hides it)
  const { result: shownRun, keep } = useRunResult<NumberingRun>(request);
  const run = useNumberingParallelsRun({
    mutation: {
      onSuccess: (out) => {
        keep(out);
        invalidate();
      },
    },
  });
  const columns = useMemo<Column<CopyRow>[]>(
    () => [
      { id: "plot", header: t("parallelTrials.col.newPlot"), value: (c) => plotKey(c), className: "mono" },
      {
        id: "source",
        header: t("parallelTrials.col.source"),
        value: (c) => plotKey({ kis: c.source_kis, parc: c.source }),
        className: "mono",
      },
      { id: "name", header: t("parallelTrials.col.name"), value: (c) => c.name ?? "" },
      { id: "code", header: t("parallelTrials.col.code"), value: (c) => c.kod ?? "", className: "mono" },
    ],
    [t],
  );
  // the first copies only (the counts are the whole plan's): the sort and filters apply to these
  const shown = useMemo(() => result?.copies.slice(0, SHOWN), [result]);
  const table = useDataTable("season.parallels.copies", columns, shown, { partial: (result?.copies.length ?? 0) > SHOWN });
  const firsts = Object.fromEntries((result?.parallels ?? []).map((p) => [p.kis, p.first]));
  const go = (s: Partial<{ kis: string; next: boolean }>) => void navigate({ search: (x) => ({ ...x, ...s }) });
  return (
    <div className="page fill">
      <PageHeader title={t("parallelTrials.title")} subtitle={t("parallelTrials.subtitle")} season />
      <div className="stack grow" style={{ gap: 14, minHeight: 0 }}>
        <section className="card pad">
          <div className="hstack" style={{ gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
            <YearToggle season={season} next={next} onChange={(n) => go({ next: n, kis: undefined })} />
            <Input.Wrapper size="xs" label={t("parallelTrials.source")}>
              <SegmentedControl
                size="xs"
                value={source}
                onChange={(v) => setSource(v as Source)}
                data={(["trial", "query"] as const).map((s) => ({ value: s, label: t(`parallelTrials.sourceMode.${s}`) }))}
              />
            </Input.Wrapper>
            {source === "trial" ? (
              <>
                <Select
                  id="par-kis"
                  placeholder={hint("searchSelect")}
                  size="xs"
                  w={200}
                  searchable
                  label={t("parallelTrials.sourceTrial")}
                  value={kis}
                  onChange={(v) => go({ kis: v ?? undefined })}
                  data={trials.map((x) => ({ value: x.kis, label: `${x.kis} · ${x.first}–${x.last}` }))}
                />
                {kis && <span className="sm mu">{t("parallelTrials.numberedPlots", { count: formatNumber(plots.length) })}</span>}
              </>
            ) : (
              <SavedQuerySelect
                value={query}
                onChange={(id) => setQuery(id)}
                w={260}
                required
                description={t("parallelTrials.queryHelp", { ev })}
              />
            )}
            <Checkbox
              size="xs"
              label={t("parallelTrials.linkToSource")}
              checked={mainLine}
              onChange={(e) => setMainLine(e.currentTarget.checked)}
            />
          </div>
          {source === "query" && result?.query_rows != null && (
            <p className={result.query_truncated ? "sm note warn" : "sm mu"}>
              {result.query_truncated
                ? t("parallelTrials.queryTruncated", { count: formatNumber(result.query_rows) })
                : t("parallelTrials.queryResult", { count: formatNumber(result.sources), rows: formatNumber(result.query_rows) })}
              {(result.query_skipped ?? 0) > 0 &&
                ` · ${t("parallelTrials.queryLeftOut", { count: formatNumber(result.query_skipped ?? 0) })}`}
            </p>
          )}
        </section>
        <section className="card pad stack" style={{ gap: 8 }}>
          <div className="hstack" style={{ gap: 8 }}>
            <span className="sec grow">{t("parallelTrials.experiments")}</span>
            <Button size="compact-xs" variant="default" onClick={() => setParallels([...parallels, nextParallel(parallels)])}>
              {t("parallelTrials.addExperiment")}
            </Button>
          </div>
          <ParallelTable parallels={parallels} lines={source === "trial" ? plots : null} firsts={firsts} onChange={setParallels} />
          <p className="sm mu">{t("parallelTrials.codeHelp")}</p>
          <p className="sm mu">{t("parallelTrials.laterHelp")}</p>
          <p className="sm mu">
            {t("parallelTrials.whileNumbering")} <Link to="/season/plot-numbering">{t("nav.season.plotNumbering")}</Link>
          </p>
        </section>
        <QueryFeedback busy={preview.busy} />
        {preview.error && !preview.busy && <p className="note err sm">{errorMessage(preview.error)}</p>}
        {result && (
          <section className="card stack grow" style={{ minHeight: 0, gap: 0, opacity: preview.busy ? 0.6 : 1 }}>
            <div className="hstack panel-head" style={{ gap: 8 }}>
              <span className="sec grow">
                {result.parallels
                  .map((p) => t("parallelTrials.range", { kis: p.kis, first: p.first, last: p.last, count: p.plots }))
                  .join(" · ")}
              </span>
              {taken.size > 0 && <span className="chip r">{t("parallelTrials.taken", { count: taken.size })}</span>}
            </div>
            <DataTable
              table={table}
              className="grow"
              rowKey={(c) => plotKey(c)}
              rowProps={(c) => ({ className: taken.has(plotKey(c)) ? "err" : undefined })}
            />
            {result.copies.length > SHOWN && (
              <p className="sm mu pad">{t("parallelTrials.more", { count: formatNumber(result.copies.length - SHOWN) })}</p>
            )}
          </section>
        )}
        {shownRun ? (
          <ResultBanner operation={shownRun.operation} after="plot_numbering">
            <b>{t("parallelTrials.done", { count: shownRun.written })}</b>
          </ResultBanner>
        ) : (
          <RunBar
            requirements={[
              source === "trial"
                ? { label: t("parallelTrials.needTrial"), done: !!kis, field: "par-kis" }
                : { label: t("parallelTrials.needQuery"), done: !!chosenQuery },
              { label: t("parallelTrials.needExperiments"), done: complete },
              { label: t("queries.source.needsAllRows"), done: !result?.query_truncated },
              { label: t("parallelTrials.needFreeNumbers", { count: taken.size }), done: taken.size === 0 },
            ]}
            summary={result && <b>{t("parallelTrials.willCreate", { count: result.copies.length })}</b>}
            undoable
            busy={preview.busy || !result?.copies.length}
            run={{
              label: t("parallelTrials.create", { count: result?.copies.length ?? 0 }),
              loading: run.isPending,
              onClick: () => request && result && run.mutate({ data: { ...request, token: result.token } }),
            }}
          />
        )}
      </div>
    </div>
  );
}
